import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/** Delegates purchases to the active buy box; never creates its own cart request. */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];
  #abort;
  #intersection;
  #buttonObserver;
  #overlayObserver;
  #button;
  #purchaseBlock;
  #quantity = 1;
  #passed = false;
  #footerVisible = false;
  #variantPending = false;
  #nativePending = false;
  #resultTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const section = this.closest('.shopify-section');
    section?.addEventListener('mc:purchase-state', this.#sync, { signal });
    section?.addEventListener(ThemeEvents.variantSelected, this.#variantSelected, { signal });
    section?.addEventListener(ThemeEvents.variantUpdate, this.#variantUpdated, { signal });
    section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#sync, { signal });
    section?.addEventListener('change', this.#sync, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#cartFinished, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#cartFinished, { signal });
    document.addEventListener('toggle', this.#visibility, { signal, capture: true });
    // The native cart drawer toggles body.mc-cart-drawer-open. Observe attributes only, not the page subtree.
    this.#overlayObserver = new MutationObserver(this.#visibility);
    this.#overlayObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
    this.#quantity = Number(this.dataset.initialQuantity) || 1;
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#intersection?.disconnect();
    this.#buttonObserver?.disconnect();
    this.#overlayObserver?.disconnect();
    clearTimeout(this.#resultTimer);
  }

  #source() {
    const section = this.closest('.shopify-section');
    const membership = section?.querySelector('.mbb');
    if (membership) {
      const state = membership.getStickyPurchaseState?.();
      return { ...state, block: membership, custom: true };
    }
    const form = section?.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
    const quantity = Number(form?.querySelector('input[name="quantity"]')?.value) || this.#quantity;
    return {
      block: form?.closest('.buy-buttons-block') || form,
      button: form?.querySelector('[ref="addToCartButton"]'),
      quantity,
      amount: Number(this.dataset.unitPrice) * quantity,
      available: this.dataset.variantAvailable === 'true',
      option: 'Buy now',
      membershipNote: '',
      custom: false
    };
  }

  #sync = () => {
    if (!this.isConnected) return;
    const state = this.#source();
    if (!state.button || !state.block) {
      this.#passed = false;
      this.#visibility();
      return;
    }
    if (state.block !== this.#purchaseBlock) this.#observePosition(state.block);
    if (state.button !== this.#button) {
      this.#buttonObserver?.disconnect();
      this.#button = state.button;
      this.#buttonObserver = new MutationObserver(this.#sync);
      this.#buttonObserver.observe(this.#button, { attributes: true, attributeFilter: ['disabled', 'class'], childList: true, subtree: true });
    }
    this.#quantity = state.quantity || 1;
    const bar = this.refs.stickyBar;
    const button = this.refs.addToCartButton;
    button.disabled = !state.available || state.button.disabled || this.#variantPending || this.#nativePending;
    button.setAttribute('aria-busy', String(state.button.classList.contains('mbb__button--loading') || this.#nativePending));
    const label = button.querySelector('.add-to-cart-text__content > span');
    if (label && !this.#nativePending) label.textContent = state.available ? (state.subscription ? 'Subscribe' : 'Add to cart') : 'Sold out';
    if (state.custom && label && (state.button.disabled || state.button.classList.contains('mbb__button--loading'))) {
      label.textContent = state.button.classList.contains('mbb__button--loading') ? 'Adding…' : state.button.textContent.trim();
    }
    this.refs.quantityDisplay.style.display = 'none';
    this.refs.quantityNumber.textContent = String(this.#quantity);
    const variant = this.querySelector('.sticky-add-to-cart__variant');
    const variantLabel = variant && !variant.style.display ? variant.textContent.trim() : '';
    bar.querySelector('[data-purchase-selection]').textContent = [variantLabel, state.option, `Qty ${this.#quantity}`].filter(Boolean).join(' · ');
    const note = bar.querySelector('[data-membership-note]');
    note.textContent = state.membershipNote || '';
    note.hidden = !state.membershipNote;
    if (Number.isFinite(state.amount)) {
      bar.querySelector('.sticky-add-to-cart__price').textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: this.dataset.currency || 'GBP'
      }).format(state.amount / 100);
    }
    this.#visibility();
  };

  #observePosition(block) {
    this.#intersection?.disconnect();
    this.#purchaseBlock = block;
    const footer = document.querySelector('footer');
    this.#intersection = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.target === block) this.#passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        else this.#footerVisible = entry.isIntersecting;
      }
      this.#visibility();
    });
    this.#intersection.observe(block);
    if (footer) this.#intersection.observe(footer);
  }

  #visibility = () => {
    const overlay = document.body.classList.contains('mc-cart-drawer-open') || !!document.querySelector('dialog[open], [role="dialog"][aria-modal="true"].is-visible');
    const visible = this.#passed && !this.#footerVisible && !overlay;
    this.refs.stickyBar.dataset.stuck = String(visible);
    this.refs.stickyBar.inert = !visible;
  };

  handleAddToCartClick = () => {
    this.#sync();
    const state = this.#source();
    if (!state.button || this.refs.addToCartButton.disabled) return;
    if (!state.custom) {
      this.#nativePending = true;
      this.refs.addToCartButton.disabled = true;
      this.refs.addToCartButton.querySelector('.add-to-cart-text__content > span').textContent = 'Adding…';
    }
    state.button.click();
    // Native browser/form validation can reject a click before any cart event is emitted.
    if (!state.custom) queueMicrotask(() => {
      if (this.#nativePending && !state.button.disabled) {
        this.#nativePending = false;
        this.#sync();
      }
    });
  };

  #variantSelected = () => {
    this.#variantPending = true;
    this.refs.addToCartButton.disabled = true;
  };

  #variantUpdated = event => {
    if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
    const next = event.detail.data.html?.querySelector('sticky-add-to-cart');
    this.#variantPending = false;
    if (next) {
      const bar = next.querySelector('[ref="stickyBar"]');
      if (bar) morph(this.refs.stickyBar, bar, { childrenOnly: true });
      this.dataset.variantAvailable = next.dataset.variantAvailable;
      this.dataset.currentVariantId = next.dataset.currentVariantId;
      this.dataset.unitPrice = next.dataset.unitPrice;
    }
    queueMicrotask(this.#sync);
  };

  #cartFinished = event => {
    if (!this.#nativePending) return;
    const data = event.detail?.data;
    if (data?.productId && String(data.productId) !== this.dataset.productId) return;
    this.#nativePending = false;
    this.#sync();
    const label = this.refs.addToCartButton.querySelector('.add-to-cart-text__content > span');
    label.textContent = event.type === ThemeEvents.cartError || data?.didError ? 'Try again' : 'Added';
    clearTimeout(this.#resultTimer);
    this.#resultTimer = setTimeout(this.#sync, 1500);
  };
}

if (!customElements.get('sticky-add-to-cart')) customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
